import React from "react";
import { Link } from "react-router-dom";

const categories = [
  {
    title: "Pediatric Clinical Studies",
    description: "Gut microbiome research in paediatric epilepsy populations, including case-control and intervention designs.",
    link: "/category/child",
    tag: "Pediatric",
  },
  {
    title: "Adult Clinical Studies",
    description: "Studies examining microbiome composition and diversity in adults living with epilepsy.",
    link: "/category/adult",
    tag: "Adult",
  },
  {
    title: "Preclinical Animal Models",
    description: "Mechanistic investigations using rodent and other animal models of seizure disorders.",
    link: "/category/animal",
    tag: "Preclinical",
  },
];

export default function Home() {
  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>

      {/* Hero */}
      <div style={{
        position: "relative",
        backgroundImage: "url('/bg.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "5rem 2rem 4rem",
        color: "#fff",
      }}>
        <div style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(160deg, rgba(10,37,64,0.88) 0%, rgba(13,59,110,0.82) 100%)",
        }} />
        <div style={{ position: "relative", zIndex: 1, maxWidth: "860px", margin: "0 auto", display: "flex", gap: "3rem", alignItems: "center", flexWrap: "wrap" }}>
          <img src="/logo.png" alt="SeizureBiomeDB" style={{ width: 140, borderRadius: 16, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#93c5e8", marginBottom: "0.75rem" }}>
              University of Calgary
            </div>
            <h1 style={{ fontSize: "2.25rem", fontWeight: 700, marginBottom: "1rem", lineHeight: 1.2 }}>
              SeizureBiomeDB
            </h1>
            <p style={{ fontSize: "1.05rem", lineHeight: 1.75, color: "#c8dff0", marginBottom: "1.75rem", maxWidth: "560px" }}>
              A curated, open-access repository linking gut microbiome alterations with seizure disorders.
              Consolidates findings across paediatric, adult, and preclinical studies to support integrated research.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              <Link to="/search" style={{
                display: "inline-block",
                background: "#0079C0",
                color: "#fff",
                padding: "0.75rem 1.75rem",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: "0.95rem",
                textDecoration: "none",
                border: "1px solid #0079C0",
              }}>
                Search Studies
              </Link>
              <Link to="/about/seizurebiomedb" style={{
                display: "inline-block",
                background: "transparent",
                color: "#c8dff0",
                padding: "0.75rem 1.75rem",
                borderRadius: 8,
                fontWeight: 500,
                fontSize: "0.95rem",
                textDecoration: "none",
                border: "1px solid rgba(255,255,255,0.2)",
              }}>
                About the Database
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div style={{ maxWidth: "980px", margin: "0 auto", padding: "3.5rem 2rem" }}>
        <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#0a2540", marginBottom: "0.4rem" }}>
          Browse by Study Category
        </h2>
        <p style={{ fontSize: "0.9rem", color: "#64748b", marginBottom: "2rem" }}>
          Studies are grouped by population type. Each entry links to the curated study detail page.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>
          {categories.map((c) => (
            <Link key={c.link} to={c.link} style={{ textDecoration: "none" }}>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  padding: "1.75rem",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  height: "100%",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "#0079C0";
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,121,192,0.1)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <span style={{
                  display: "inline-block",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#0079C0",
                  background: "rgba(0,121,192,0.08)",
                  padding: "0.25rem 0.6rem",
                  borderRadius: 4,
                  marginBottom: "0.9rem",
                }}>
                  {c.tag}
                </span>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#0a2540", marginBottom: "0.5rem" }}>
                  {c.title}
                </h3>
                <p style={{ fontSize: "0.875rem", color: "#64748b", lineHeight: 1.6 }}>
                  {c.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick info strip */}
      <div style={{ background: "#fff", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ maxWidth: "980px", margin: "0 auto", padding: "2rem", display: "flex", gap: "3rem", flexWrap: "wrap", justifyContent: "center" }}>
          {[
            { label: "Study Design", value: "Case-control, cohort & intervention studies" },
            { label: "Data Type", value: "16S rRNA Gene Sequencing · Shotgun metagenomics · ITS sequencing" },
            { label: "Coverage", value: "Paediatric · Adult · Animal models" },
          ].map((item) => (
            <div key={item.label} style={{ textAlign: "center", minWidth: 200 }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "#94a3b8", marginBottom: "0.35rem" }}>
                {item.label}
              </div>
              <div style={{ fontSize: "0.9rem", fontWeight: 500, color: "#1e293b" }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer style={{ padding: "2.5rem 2rem", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" }}>
        <p>© {new Date().getFullYear()} SeizureBiomeDB · Hotchkiss Brain Institute, University of Calgary</p>
        <p style={{ marginTop: "0.35rem" }}>Advancing understanding of the gut–brain axis in epilepsy research.</p>
      </footer>
    </div>
  );
}
